import { useCallback, useEffect, useMemo, useState } from 'react';
import { Quote, QuoteUpdatePayload } from '../types';
import * as quotesService from '../services/quotes.service';
import { DateRangeFilter } from '../services/quotes.service';
import { withAuthRetry } from '../utils/withAuthRetry';

interface UseQuotesProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  canManageQuotes: boolean;
}

export function useQuotes({ accessToken, refreshToken, attemptRefresh, canManageQuotes }: UseQuotesProps) {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [allQuoteDates, setAllQuoteDates] = useState<Date[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [quoteDetailsOpen, setQuoteDetailsOpen] = useState(false);
  const [quotesFilter, setQuotesFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [quotesDateRange, setQuotesDateRange] = useState<DateRangeFilter | undefined>(() => {
    const today = new Date();
    return { from: today, to: today };
  });
  const [quotesSearch, setQuotesSearch] = useState('');
  const [quoteEditMode, setQuoteEditMode] = useState(false);
  const [isUpdatingQuote, setIsUpdatingQuote] = useState(false);

  const filteredQuotes = useMemo(() => {
    if (!quotesSearch.trim()) {
      return quotes;
    }
    const searchLower = quotesSearch.toLowerCase().trim();
    return quotes.filter((quote) => {
      const createdDate = new Date(quote.createdAt);
      const dateStr = createdDate.toLocaleDateString('pt-BR');
      const isoDateStr = createdDate.toISOString().split('T')[0];

      return (
        quote.quoteNumber.toLowerCase().includes(searchLower) ||
        quote.customerName.toLowerCase().includes(searchLower) ||
        quote.customerEmail.toLowerCase().includes(searchLower) ||
        (quote.customerCompany && quote.customerCompany.toLowerCase().includes(searchLower)) ||
        (quote.customerPhone && quote.customerPhone.includes(searchLower)) ||
        dateStr.includes(searchLower) ||
        isoDateStr.includes(searchLower)
      );
    });
  }, [quotes, quotesSearch]);

  const fetchQuotes = useCallback(async (): Promise<{ success: boolean; error?: string; data?: Quote[] }> => {
    if (!accessToken || !canManageQuotes) return { success: false, error: 'Não autorizado' };

    setQuotesLoading(true);

    try {
      const data = await withAuthRetry(
        () => quotesService.fetchQuotes(accessToken!, { status: quotesFilter, dateRange: quotesDateRange }),
        refreshToken,
        attemptRefresh
      );
      setQuotes(data);
      return { success: true, data };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro inesperado ao buscar orçamentos'
      };
    } finally {
      setQuotesLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, canManageQuotes, quotesFilter, quotesDateRange]);

  const markAsViewed = useCallback(
    async (quoteId: string): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Não autenticado' };

      // Optimistically update the quote in local state to remove "Novo" badge immediately
      setQuotes(prevQuotes =>
        prevQuotes.map(q =>
          q.id === quoteId ? { ...q, isViewed: true } : q
        )
      );

      try {
        await withAuthRetry(
          () => quotesService.markQuoteAsViewed(quoteId, accessToken!),
          refreshToken,
          attemptRefresh
        );
        await fetchQuotes();
        return { success: true };
      } catch (err) {
        // Revert optimistic update on error
        await fetchQuotes();
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao marcar orçamento como visto'
        };
      }
    },
    [accessToken, refreshToken, attemptRefresh, fetchQuotes]
  );

  const exportPDF = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken) return { success: false, error: 'Não autenticado' };

    try {
      const blob = await quotesService.exportQuotesPDF(accessToken);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orcamentos-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: 'Erro ao exportar orçamentos'
      };
    }
  }, [accessToken]);

  const openQuoteDetails = useCallback((quote: Quote) => {
    setSelectedQuote(quote);
    setQuoteDetailsOpen(true);
    setQuoteEditMode(false);
  }, []);

  const enterEditMode = useCallback(() => {
    setQuoteEditMode(true);
  }, []);

  const cancelEdit = useCallback(() => {
    setQuoteEditMode(false);
  }, []);

  const updateQuote = useCallback(
    async (quoteId: string, payload: QuoteUpdatePayload): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Não autenticado' };

      setIsUpdatingQuote(true);

      try {
        const updatedQuote = await withAuthRetry(
          () => quotesService.updateQuote(quoteId, payload, accessToken!),
          refreshToken,
          attemptRefresh
        );

        // Update quotes list
        setQuotes(prevQuotes =>
          prevQuotes.map(q => (q.id === updatedQuote.id ? updatedQuote : q))
        );

        // Update selected quote
        setSelectedQuote(updatedQuote);

        // Exit edit mode
        setQuoteEditMode(false);

        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao atualizar orçamento'
        };
      } finally {
        setIsUpdatingQuote(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh]
  );

  // Fetch all quote dates for calendar highlighting (only once on mount)
  const fetchAllQuoteDates = useCallback(async () => {
    if (!accessToken || !canManageQuotes) return;

    try {
      // Fetch all quotes without date filter to get all dates
      const allQuotes = await quotesService.fetchQuotes(accessToken, {});
      const dates = allQuotes.map(q => {
        const d = new Date(q.createdAt);
        // Normalize to start of day for comparison
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
      });
      // Remove duplicates
      const uniqueDates = dates.filter((date, index, self) =>
        index === self.findIndex(d => d.getTime() === date.getTime())
      );
      setAllQuoteDates(uniqueDates);
    } catch (err) {
      console.error('Error fetching quote dates:', err);
    }
  }, [accessToken, canManageQuotes]);

  // Automatically refetch quotes when filter changes or when accessToken becomes available
  useEffect(() => {
    if (accessToken && canManageQuotes) {
      fetchQuotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, canManageQuotes, quotesFilter, quotesDateRange]);

  // Fetch all quote dates on mount
  useEffect(() => {
    if (accessToken && canManageQuotes) {
      fetchAllQuoteDates();
    }
  }, [accessToken, canManageQuotes]);

  return {
    // State
    quotes: filteredQuotes,
    setQuotes,
    quotesLoading,
    selectedQuote,
    setSelectedQuote,
    quoteDetailsOpen,
    setQuoteDetailsOpen,
    quotesFilter,
    setQuotesFilter,
    quotesDateRange,
    setQuotesDateRange,
    quotesSearch,
    setQuotesSearch,
    quoteEditMode,
    isUpdatingQuote,
    allQuoteDates,

    // Actions
    fetchQuotes,
    markAsViewed,
    exportPDF,
    openQuoteDetails,
    enterEditMode,
    cancelEdit,
    updateQuote
  };
}
